import React, { useEffect, useState } from 'react';
import { ReturnHeaders } from '@src/components/shared/ReturnHeaders';
import { ReturnButtons } from '@src/components/shared/ReturnButtons';
import {
  goToPreviousStep,
  goToNextStep,
  setChosenReturnMethods,
} from '@src/stores/newFlowStore';
import {
  setReturnShipments,
  setSelectedItems,
  useReturnStore,
} from '@src/stores/returnStore';
import {
  MAX_WEIGHT_LIMIT,
  RETURN_METHOD,
  ReturnMethodErrorType,
  ReturnMethodType,
  isClientSideOnlyMethod,
} from '@src/constants/general';
import OrderReturnMethodOptions from '@src/components/return-method/OrderReturnMethodOptions';
import SplitReturnMethodOptions from '@src/components/return-method/SplitReturnMethodOptions';
import {
  buildRefundEstimationRequest,
  checkValidatorFraudType,
  generateInStoreShipment,
  PolicyValidatorErrors,
} from '@src/utils';
import {
  disableAbandonmentSurvey,
  enableLORPAbandonmentSurvey,
  enableReturnAbandonmentSurvey,
} from '@src/utils/storage-helper';
import { isCookieEnabled } from '@src/utils/cookie-helper';
import { isProdEnvironment } from '@src/config/timeout';
import {
  fetchRefundEstimationAPI,
  TIMEOUT_ERROR_CODE,
} from '@src/utils/api-helpers';
import { setRefundValues, useSummaryStore } from '@src/stores/summaryStore';
import {
  getFeatureSwitchStatic,
  getTotalUPSWeight,
  getUniqueMethods,
  redirectToOrderDetails,
} from '@src/utils/general';
import { useProcessValidator } from '@src/hooks/useProcessValidator';
import useKeepItCreateReturn from '@src/hooks/useKeepItCreateReturn';
import useUpsGrouping from '@src/hooks/useUpsGrouping';
import ItemsNotProcessedCard from '@src/components/shared/ItemsNotProcessedCard';
import FraudModal from '@src/components/shared/fraud/FraudModal';
import PolicyValidatorAlert from '@src/components/select-items/PolicyValidatorAlert';
import useScrollableError from '@src/hooks/useScrollableError';
import WholeOrderMethodError from '@src/components/return-method/errors/WholeOrderMethodError';
import { ItemToReturn, SelectedCardType } from '@src/models/returns';
import {
  TagOnOrderSelectPerItemMethodChosen,
  TagOnReturnMethodPerItemChosen,
  TagReturnQuantityReasonSubmission,
  TagReturnMethodContinue,
  TagScheduleRPMReturnPickupSelected,
} from '@src/utils/analytics-tagging';
import { getReturnMethodSelectionMode } from '@src/utils/returnFlow';
import {
  isROSEnabled,
  isScheduledPickupEnabled,
} from '@src/constants/fs-values';

const IN_STORE_QTY_REASON_SETTINGS =
  'in store return quantity and reason selected';

const isWholeOrderQuantityReasonMethod = (
  method: SelectedCardType,
): method is ReturnMethodType =>
  method === RETURN_METHOD.IN_STORE ||
  method === RETURN_METHOD.SORIS ||
  method === RETURN_METHOD.UPS_DROP_OFF ||
  method === RETURN_METHOD.LORP;

const getWholeOrderQuantityReasonSettings = (method: ReturnMethodType) =>
  method === RETURN_METHOD.IN_STORE || method === RETURN_METHOD.SORIS
    ? IN_STORE_QTY_REASON_SETTINGS
    : undefined;

const SelectReturnMethodStep = () => {
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const returnEligibilityDetails = useReturnStore(
    (state) => state.returnEligibilityDetails,
  );
  const refundValues = useSummaryStore((state) => state.refundValues);
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );
  const orderDetailsPath = useReturnStore((state) => state.orderDetailsPath);
  const militaryExchange = useReturnStore((state) => state.militaryExchange);

  // For returns that want to return less than 4 SKU items, we will skip the WholeOrderStep step and go straight to PerItemStep
  // Exception: APO/FPO eligible orders always use WholeOrderStep (no per-item selection)
  const selectedItems = useReturnStore((state) => state.selectedItems);
  const { isApoFpoEligible, shouldSkipFirstStep, isStandardWholeOrderFlow } =
    getReturnMethodSelectionMode(selectedItems);

  // Change default to no pre-selection
  const initialSelectedCard: SelectedCardType = '';
  const [selectedCard, setSelectedCard] =
    useState<SelectedCardType>(initialSelectedCard);
  const [step, setStep] = useState<'WholeOrderStep' | 'PerItemStep'>(
    shouldSkipFirstStep ? 'PerItemStep' : 'WholeOrderStep',
  );

  // Scrollable error for whole order step
  const {
    errorRef: wholeOrderErrorRef,
    showAndScrollToError: showAndScrollToWholeOrderError,
    showError: showWholeOrderErrorWithScroll,
    hideScrollableError: hideWholeOrderError,
    errorType: wholeOrderErrorType,
  } = useScrollableError<ReturnMethodErrorType>();

  // Scrollable error for per-item step
  const {
    errorRef: perItemErrorRef,
    showAndScrollToFirstError: showAndScrollToFirstPerItemError,
    showError: showPerItemErrorsWithScroll,
    hideScrollableError: hidePerItemErrors,
  } = useScrollableError('[data-testid="item-return-method-error"]');

  // past selections
  const [stringRefundRequest, setStringRefundRequest] = useState<string>('');

  // Bridges the Continue button's loading state across the PolicyValidator →
  // Keep It submit handoff. Those phases use separate flags updated in separate
  // React commits, so without this the `loading` prop would dip to false for one
  // render and SUI LoadingButton would restart its spinner animation. Set true at
  // the start of the async continue action and reset on every exit except the
  // Keep It submit (which keeps it true until it navigates or falls back), so the
  // button never stays stuck loading if the step doesn't unmount.
  const [isContinuing, setIsContinuing] = useState(false);

  // could remove this alert state if not needed anymore

  const proceedToNextStep = () => {
    const refundRequest = buildRefundEstimationRequest(
      orderDetailsV2,
      selectedItems,
    );
    const newStringRefundRequest = JSON.stringify(refundRequest.lineItems);
    if (refundValues && stringRefundRequest === newStringRefundRequest) {
      goToNextStep();
      return;
    }
    setStringRefundRequest(newStringRefundRequest);
    fetchRefundEstimationAPI(refundRequest).then((refundAmount) =>
      setRefundValues(refundAmount),
    );
    goToNextStep();
  };

  // PolicyValidator
  const {
    processPolicyValidator,
    renderModal,
    returnFraudType,
    policyValidatorAlert,
    setPolicyValidatorAlert,
    isPolicyValidatorLoading,
  } = useProcessValidator({
    orderDetailsV2,
    returnEligibilityDetails,
    isMilitaryExchangeGuest,
    proceedToNextStep,
  });

  // Keep It: qualifying UPS returns submit directly and skip the box/refund/finalize steps
  const {
    submitKeepItReturn,
    retryKeepItSubmit,
    status: keepItStatus,
    isSubmitting: isKeepItSubmitting,
    isRetrying: isKeepItRetrying,
    hasRetried: hasKeepItRetried,
  } = useKeepItCreateReturn();

  // Shipping grouping + box minimum, fetched behind the same Continue press
  const { fetchUpsGrouping, abandonGrouping, isGroupingLoading } =
    useUpsGrouping();

  /**
   * Fetches which drop-off lines ship together and the box minimum each group
   * needs, then moves on. Every route into the box step goes through here, so
   * the picker is never reached without a floor.
   *
   * `isContinuing` is held by the caller across this, so the spinner runs
   * unbroken from the validator call through to here.
   */
  const groupAndProceed = async (items: ItemToReturn[]) => {
    if (isROSEnabled()) {
      const grouped = await fetchUpsGrouping(items);
      // A superseded attempt has nothing to say and nothing to advance: the
      // customer has left the step, so an error would be shown to nobody and
      // the writes would land on a component that is gone.
      if (grouped === 'superseded') return;
      if (grouped === 'failed') {
        setPolicyValidatorAlert(PolicyValidatorErrors.NETWORK);
        setIsContinuing(false);
        return;
      }
    }
    setIsContinuing(false);
    proceedToNextStep();
  };

  const handleContinueButton = () => {
    hideWholeOrderError();
    // Only check selectedCard if we're on WholeOrderStep
    if (step === 'WholeOrderStep' && selectedCard === '') {
      showAndScrollToWholeOrderError();
      return;
    }

    if (
      step === 'WholeOrderStep' &&
      selectedCard !== 'Split' &&
      selectedCard !== ''
    ) {
      setChosenReturnMethods([selectedCard]);
      const modifiedItems = selectedItems.map((item) => ({
        ...item,
        returnMethod: selectedCard,
      }));
      setSelectedItems(modifiedItems);
      if (
        isStandardWholeOrderFlow &&
        isWholeOrderQuantityReasonMethod(selectedCard)
      ) {
        TagReturnQuantityReasonSubmission(
          modifiedItems,
          militaryExchange,
          getWholeOrderQuantityReasonSettings(selectedCard),
        );
      }
      onStepFinish(modifiedItems);
      TagReturnMethodContinue(selectedCard);
      // Schedule RPM (5.0): additionally tag when Return Pickup is chosen and
      // the Schedule RPM flow is active. Fires alongside the existing method tag.
      if (selectedCard === RETURN_METHOD.LORP && isScheduledPickupEnabled()) {
        TagScheduleRPMReturnPickupSelected();
      }
      return;
    }

    if (step === 'PerItemStep') {
      if (selectedItems.some((item) => !item.returnMethod)) {
        showAndScrollToFirstPerItemError();
        return;
      }
      // ROS: weight is accounted for in full by the Return Options Service, so
      // the client no longer blocks on its own aggregate. Retained for the
      // switch-off path only.
      if (
        !isROSEnabled() &&
        getTotalUPSWeight(selectedItems) >= MAX_WEIGHT_LIMIT
      ) {
        showAndScrollToWholeOrderError(
          ReturnMethodErrorType.WEIGHT_LIMIT_EXCEEDED,
        );
        return;
      }

      setChosenReturnMethods(getUniqueMethods(selectedItems));
      onStepFinish(selectedItems);
      if (isStandardWholeOrderFlow && selectedCard === 'Split') {
        TagReturnQuantityReasonSubmission(selectedItems, militaryExchange);
      }
      TagOnReturnMethodPerItemChosen(selectedItems);
      // Schedule RPM (5.0): additionally tag when the per-item selection
      // includes a Return Pickup item and the Schedule RPM flow is active.
      if (
        isScheduledPickupEnabled() &&
        selectedItems.some((item) => item.returnMethod === RETURN_METHOD.LORP)
      ) {
        TagScheduleRPMReturnPickupSelected();
      }
      return;
    }

    // selectedSplit but still at step 1
    setStep('PerItemStep');
    TagOnOrderSelectPerItemMethodChosen();
  };

  const handleBackButton = () => {
    // Back stays enabled while the grouping call is in flight, and the branch
    // below leaves the step without unmounting, so nothing else would tell the
    // call it has been abandoned. Its answer would then rewrite the shipments
    // and move the customer forward, undoing the back they just pressed.
    abandonGrouping();
    if (step === 'PerItemStep' && !shouldSkipFirstStep) {
      setStep('WholeOrderStep');
      return;
    }
    goToPreviousStep();
  };

  const onStepFinish = async (modifiedItems: ItemToReturn[]) => {
    // Check if all items are client-side only (IN_STORE or APO/FPO) - if so, skip Policy Validator entirely
    const hasAllClientSideItems = modifiedItems.every((item) =>
      isClientSideOnlyMethod(item.returnMethod),
    );

    // Enable abandonment survey based on selected return method(s)
    // Use LORP survey only when ALL items are LORP
    // Disable survey for all IN_STORE/APO_FPO items
    // Otherwise default to return survey (UPS, SORIS, or mixed)
    // Skip survey logic if qaSurveyPass cookie is set in non-prod (for QA regression testing)
    const isQaSurveyBypass =
      !isProdEnvironment() && isCookieEnabled('qaSurveyPass');
    if (!isQaSurveyBypass) {
      const allLORP = modifiedItems.every(
        (item) => item.returnMethod === RETURN_METHOD.LORP,
      );
      if (hasAllClientSideItems) {
        disableAbandonmentSurvey();
      } else if (allLORP) {
        enableLORPAbandonmentSurvey();
      } else {
        enableReturnAbandonmentSurvey();
      }
    }

    if (hasAllClientSideItems) {
      // All items are client-side only (IN_STORE or APO/FPO), no need for Policy Validator call
      // Only IN_STORE needs a pseudo-shipment; APO/FPO doesn't use returnShipments
      if (
        modifiedItems.some(
          (item) => item.returnMethod === RETURN_METHOD.IN_STORE,
        )
      ) {
        setReturnShipments([generateInStoreShipment(modifiedItems, [])]);
      }
      proceedToNextStep();
      return;
    }

    // Non-client-side methods (UPS/LORP/SORIS) require PolicyValidator before proceeding.
    // Hold the button loading state continuously across PolicyValidator and a possible
    // Keep It submit so the spinner doesn't reset at the handoff.
    setIsContinuing(true);
    const policyResponse = await processPolicyValidator(modifiedItems);
    if (!policyResponse) {
      setIsContinuing(false);
      return;
    }
    // Check for fraud has been done before, so only proceeding in case no Modal was shown.
    const verifyDecision = policyResponse?.returnService?.multiVerifyDecision;
    const fraudType = checkValidatorFraudType(verifyDecision);
    if (fraudType) {
      setIsContinuing(false);
      return;
    }

    // Keep It: qualifying UPS-only returns submit directly and skip the
    // boxes/refund/finalize steps. `isKeepIt` is server-decided; the FE switch
    // gates routing only, and we fall back to the standard flow on a non-timeout
    // failure.
    //
    // The `.every(... === UPS_DROP_OFF)` guard is required: useProcessValidator
    // filters Phase-0 In-Store items OUT of the PolicyValidator request, so the BE
    // can return isKeepIt=true while the selection still contains an In-Store item.
    // Without this guard, Keep It would submit only the UPS shipment and silently
    // drop the In-Store item. (LORP/SORIS 1.0 ARE sent to the validator, and the BE
    // forces isKeepIt=false for them, so those mixes don't need this guard.)
    const isKeepItReturn =
      getFeatureSwitchStatic('enableKeepItReturns') &&
      policyResponse?.returnService?.ssrHRTVResponse?.isKeepIt === true &&
      modifiedItems.every(
        (item) => item.returnMethod === RETURN_METHOD.UPS_DROP_OFF,
      );

    if (isKeepItReturn) {
      // Keep the spinner continuous through the createReturn submit; reset the
      // bridge flag only if we fall back to the standard flow.
      //
      // The fallback lands on the standard flow, box step included, so it needs
      // the grouping the Keep It path itself never wanted — a Keep It return
      // that succeeds skips the box step, but one that fails does not.
      submitKeepItReturn(() => groupAndProceed(modifiedItems));
      return;
    }

    await groupAndProceed(modifiedItems);
  };

  useEffect(() => {
    setStep(shouldSkipFirstStep ? 'PerItemStep' : 'WholeOrderStep');
    if (shouldSkipFirstStep) {
      TagOnOrderSelectPerItemMethodChosen();
    }
  }, [shouldSkipFirstStep, isApoFpoEligible]);

  // Hide errors when user makes selections
  useEffect(() => {
    if (selectedCard !== '') {
      hideWholeOrderError();
    }
  }, [selectedCard]);

  useEffect(() => {
    if (
      step === 'PerItemStep' &&
      selectedItems.every((item) => item.returnMethod)
    ) {
      hidePerItemErrors();
    }
  }, [selectedItems, step]);

  // Keep It createReturn timed out: we don't know whether the return was created,
  // so replace the method selection with a retry screen. "Try Again" resubmits in
  // place (no navigation back to the method step); every attempt reuses this view.
  if (keepItStatus === 'timeout') {
    // The `!isKeepItRetrying` guard matters: this branch stays mounted through the
    // retry, so without it the exit view would flash the moment "Try Again" is
    // clicked rather than after the retry resolves.
    const showReturnToOrderDetails = hasKeepItRetried && !isKeepItRetrying;
    const handleReturnToOrderDetails = () =>
      redirectToOrderDetails(orderDetailsPath, isMilitaryExchangeGuest);
    return (
      <div className="sui-max-w-[555px]">
        <ReturnHeaders title="Select your return method" />
        <ItemsNotProcessedCard
          header="UPS Drop-Off Not Processed"
          items={selectedItems}
          onRetry={retryKeepItSubmit}
          retrying={isKeepItRetrying}
          retryFailed={showReturnToOrderDetails}
          onReturnToOrderDetails={handleReturnToOrderDetails}
          code={TIMEOUT_ERROR_CODE}
        />
      </div>
    );
  }

  return (
    <div>
      <ReturnHeaders title="Select your return method" />
      {policyValidatorAlert && (
        <PolicyValidatorAlert
          alert={policyValidatorAlert}
          clearAlert={() => setPolicyValidatorAlert(PolicyValidatorErrors.NONE)}
        />
      )}

      <WholeOrderMethodError
        ref={wholeOrderErrorRef}
        showError={showWholeOrderErrorWithScroll}
        type={wholeOrderErrorType}
      />

      {step === 'WholeOrderStep' && (
        <>
          <OrderReturnMethodOptions
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
          />
        </>
      )}
      {step === 'PerItemStep' && (
        <SplitReturnMethodOptions
          showItemErrors={showPerItemErrorsWithScroll}
          ref={perItemErrorRef}
        />
      )}
      <ReturnButtons
        proceedNext={handleContinueButton}
        loading={
          isContinuing ||
          isPolicyValidatorLoading ||
          isKeepItSubmitting ||
          isGroupingLoading
        }
        proceedBack={handleBackButton}
      />
      {renderModal && (
        <FraudModal
          returnFraudType={returnFraudType}
          title={renderModal.title}
          body={renderModal.bodyMessage}
          buttonText={renderModal.buttonMessage}
          onClick={renderModal.onClick}
        />
      )}
    </div>
  );
};

export default SelectReturnMethodStep;
